import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, ImagePlus, Send, X, Loader2, Reply, Pencil, Trash2, Flag,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/MyCinemaContext";
import { useTranslate } from "../context/TranslateContext";
import {
  listDiscussions, postComment, editComment, deleteComment,
  reactComment, reportComment, uploadDiscussionPhoto, fileUrl,
} from "../lib/api";

const REACTIONS = [
  { key: "like", emoji: "\uD83D\uDC4D", label: "Like", color: "#2563eb" },
  { key: "love", emoji: "\u2764\uFE0F", label: "Love", color: "#e11d48" },
  { key: "care", emoji: "\uD83E\uDD17", label: "Care", color: "#f59e0b" },
  { key: "haha", emoji: "\uD83D\uDE06", label: "Haha", color: "#eab308" },
  { key: "wow", emoji: "\uD83D\uDE2E", label: "Wow", color: "#eab308" },
  { key: "sad", emoji: "\uD83D\uDE22", label: "Sad", color: "#eab308" },
  { key: "angry", emoji: "\uD83D\uDE21", label: "Angry", color: "#ea580c" },
];
const RX = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));
const MAX_PHOTOS = 4;
const MAX_BYTES = 20 * 1024 * 1024;

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";

const timeAgo = (iso) => {
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
};

// Flatten a comment tree into an ordered [{comment, depth}] list (non-recursive component render).
const flattenTree = (roots) => {
  const out = [];
  const walk = (node, depth) => {
    out.push({ comment: node, depth });
    (node.replies || []).forEach((r) => walk(r, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return out;
};

const Avatar = ({ name, size = 40 }) => (
  <div
    className="flex shrink-0 items-center justify-center rounded-full bg-gold/20 font-display font-semibold text-gold"
    style={{ width: size, height: size, fontSize: size * 0.4 }}
  >
    {initials(name)}
  </div>
);

// ---- Composer (new comment / reply / edit) ----
const Composer = ({ onSubmit, submitting, placeholder, autoFocus, compact, initial = "", submitLabel = "Post" }) => {
  const { t } = useTranslate();
  const { user } = useAuth();
  const [text, setText] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(t(`You can attach up to ${MAX_PHOTOS} photos`));
      return;
    }
    setUploading(true);
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(t("Only images are allowed")); continue; }
      if (f.size > MAX_BYTES) { toast.error(t("Each image must be under 20MB")); continue; }
      try {
        const up = await uploadDiscussionPhoto(f);
        setPhotos((p) => [...p, up]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("Photo upload failed"));
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    const body = text.trim();
    if (!body && !photos.length) return;
    const ok = await onSubmit({ body, photo_ids: photos.map((p) => p.id) });
    if (ok) { setText(""); setPhotos([]); }
  };

  return (
    <div className="flex gap-3" data-testid={compact ? "reply-composer" : "discussion-composer"}>
      <Avatar name={user?.name} size={compact ? 32 : 40} />
      <div className="flex-1 rounded-2xl border border-edge bg-surface/50 p-3">
        <textarea
          data-testid={compact ? "reply-input" : "discussion-input"}
          autoFocus={autoFocus}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t(placeholder || "Share your thoughts\u2026")}
          rows={compact ? 2 : 3}
          className="w-full resize-none bg-transparent font-body text-base text-cream placeholder:text-ash/70 focus:outline-none"
        />
        {photos.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-edge">
                <img src={fileUrl(p.url)} alt="" className="h-full w-full object-cover" />
                <button
                  onClick={() => setPhotos((arr) => arr.filter((x) => x.id !== p.id))}
                  className="absolute right-1 top-1 rounded-full bg-ink/70 p-0.5 text-white hover:bg-ink"
                  data-testid={`remove-photo-${p.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={photos.length >= MAX_PHOTOS || uploading}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 font-body text-sm text-ash transition-colors hover:text-gold disabled:opacity-40"
            data-testid="discussion-photo-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {t("Photo")} <span className="text-xs text-ash/60">{photos.length}/{MAX_PHOTOS}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="discussion-photo-input" />
          <button
            onClick={submit}
            disabled={submitting || uploading || (!text.trim() && !photos.length)}
            className="inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 font-body font-medium text-ink transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
            data-testid={compact ? "reply-submit" : "discussion-submit"}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {t(submitLabel)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Reaction picker ----
const ReactionControl = ({ comment, onReact }) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const cur = comment.my_reaction ? RX[comment.my_reaction] : null;
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => (cur ? onReact(cur.key) : onReact("like"))}
        onMouseEnter={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-body text-sm font-medium transition-colors hover:bg-surface"
        style={{ color: cur ? cur.color : undefined }}
        data-testid={`comment-react-${comment.id}`}
      >
        {cur ? <><span className="text-base leading-none">{cur.emoji}</span>{t(cur.label)}</> : <><span className="text-base leading-none">{RX.like.emoji}</span>{t("Like")}</>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-0 z-20 mb-1 flex gap-1 rounded-full border border-edge bg-paper p-1.5 shadow-xl"
            data-testid={`reaction-picker-${comment.id}`}
          >
            {REACTIONS.map((r) => (
              <button
                key={r.key}
                onClick={() => { onReact(r.key); setOpen(false); }}
                title={t(r.label)}
                className="text-2xl leading-none transition-transform hover:-translate-y-1 hover:scale-125"
                data-testid={`reaction-opt-${comment.id}-${r.key}`}
              >
                {r.emoji}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ReactionSummary = ({ comment }) => {
  if (!comment.reaction_total) return null;
  const top = Object.entries(comment.reactions || {})
    .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => RX[k]?.emoji).filter(Boolean);
  return (
    <div className="flex items-center gap-1 font-body text-xs text-ash" data-testid={`reaction-summary-${comment.id}`}>
      <span className="flex">{top.map((e, i) => <span key={i} className="text-sm leading-none">{e}</span>)}</span>
      <span>{comment.reaction_total}</span>
    </div>
  );
};

// ---- A single comment row (no nested rendering; replies are flattened by the parent) ----
const CommentRow = ({ comment, depth, onReact, onReply, onEdit, onDelete, onReport, busy }) => {
  const { t } = useTranslate();
  const { user, setAuthOpen } = useAuth();
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const staffBadge = comment.author_role === "admin" ? "Admin" : comment.author_role === "editor" ? "Editor" : null;
  const gate = (fn) => (...args) => { if (!user) { setAuthOpen(true); return; } fn(...args); };
  const indent = Math.min(depth, 4);

  return (
    <div
      className={`flex gap-3 ${depth ? "border-l border-edge pl-4" : ""}`}
      style={depth ? { marginLeft: indent * 8 } : undefined}
      data-testid={`comment-${comment.id}`}
    >
      <Avatar name={comment.author_name} size={depth ? 32 : 40} />
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl border border-edge bg-surface/40 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-display text-base font-semibold text-cream" data-testid={`comment-author-${comment.id}`}>
              {comment.author_name}
            </span>
            {staffBadge && (
              <span className="rounded-full bg-gold/15 px-2 py-0.5 font-body text-[10px] uppercase tracking-wide text-gold">
                {t(staffBadge)}
              </span>
            )}
            <span className="font-body text-xs text-ash">
              {"\u00B7"} {timeAgo(comment.created_at)}{comment.edited ? ` \u00B7 ${t("edited")}` : ""}
            </span>
          </div>
          {editing ? (
            <div className="mt-2">
              <Composer
                compact autoFocus initial={comment.body} submitLabel="Save" placeholder="Edit your comment…"
                submitting={busy}
                onSubmit={async ({ body }) => { const ok = await onEdit(comment.id, body); if (ok) setEditing(false); return ok; }}
              />
            </div>
          ) : (
            <>
              {comment.body && (
                <p className="mt-1 whitespace-pre-wrap font-body text-base leading-relaxed text-cream" data-testid={`comment-body-${comment.id}`}>
                  {comment.body}
                </p>
              )}
              {comment.photos?.length > 0 && (
                <div className={`mt-2 grid gap-2 ${comment.photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {comment.photos.map((p) => (
                    <a key={p.id} href={fileUrl(p.url)} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-edge">
                      <img src={fileUrl(p.url)} alt="" className="max-h-72 w-full object-cover transition-transform hover:scale-[1.03]" data-testid={`comment-photo-${p.id}`} />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-1 pl-1">
          <ReactionControl comment={comment} onReact={gate((k) => onReact(comment.id, k))} />
          <button onClick={gate(() => setReplying((v) => !v))} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-body text-sm text-ash transition-colors hover:text-gold" data-testid={`comment-reply-${comment.id}`}>
            <Reply className="h-3.5 w-3.5" /> {t("Reply")}
          </button>
          {comment.can_edit && !editing && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-body text-sm text-ash transition-colors hover:text-gold" data-testid={`comment-edit-${comment.id}`}>
              <Pencil className="h-3.5 w-3.5" /> {t("Edit")}
            </button>
          )}
          {comment.can_delete && (
            <button onClick={() => onDelete(comment.id)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-body text-sm text-ash transition-colors hover:text-red-500" data-testid={`comment-delete-${comment.id}`}>
              <Trash2 className="h-3.5 w-3.5" /> {t("Delete")}
            </button>
          )}
          {user && !comment.can_edit && (
            <button onClick={() => onReport(comment.id)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-body text-sm text-ash transition-colors hover:text-gold" data-testid={`comment-report-${comment.id}`}>
              <Flag className="h-3.5 w-3.5" /> {t("Report")}
            </button>
          )}
          <ReactionSummary comment={comment} />
        </div>

        {replying && (
          <div className="mt-2">
            <Composer
              compact autoFocus submitting={busy} submitLabel="Reply" placeholder={`Reply to ${comment.author_name}\u2026`}
              onSubmit={async (payload) => { const ok = await onReply(comment.id, payload); if (ok) setReplying(false); return ok; }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const Discussion = ({ entityType, entityId, title = "Discussion" }) => {
  const { t } = useTranslate();
  const { user, setAuthOpen } = useAuth();
  const [data, setData] = useState({ comments: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const d = await listDiscussions(entityType, entityId);
      setData(d);
    } catch { /* keep prior */ }
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const create = async ({ body, photo_ids }) => {
    setBusy(true);
    try {
      await postComment({ entity_type: entityType, entity_id: entityId, body, photo_ids });
      await load();
      toast.success(t("Posted"));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not post"));
      return false;
    } finally { setBusy(false); }
  };

  const reply = async (parentId, { body, photo_ids }) => {
    setBusy(true);
    try {
      await postComment({ entity_type: entityType, entity_id: entityId, parent_id: parentId, body, photo_ids });
      await load();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || t("Could not reply")); return false; }
    finally { setBusy(false); }
  };

  const onEdit = async (id, body) => {
    setBusy(true);
    try { await editComment(id, body); await load(); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || t("Could not edit")); return false; }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("Delete this comment?"))) return;
    try { await deleteComment(id); await load(); toast.success(t("Removed")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Could not delete")); }
  };

  const onReact = async (id, type) => {
    try { await reactComment(id, type); await load(); }
    catch { toast.error(t("Could not react")); }
  };

  const onReport = async (id) => {
    try { await reportComment(id); toast.success(t("Reported to moderators. Thank you.")); }
    catch { toast.error(t("Could not report")); }
  };

  const rows = flattenTree(data.comments);

  return (
    <section className="mx-auto max-w-3xl px-6 py-14" data-testid="discussion-section" id="discussion">
      <div className="mb-6 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-gold" />
        <h2 className="font-display text-2xl font-semibold text-cream">{t(title)}</h2>
        <span className="font-body text-sm text-ash">{"\u00B7"} {data.count}</span>
      </div>

      {user ? (
        <div className="mb-8"><Composer submitting={busy} onSubmit={create} placeholder="Share your thoughts with the community…" /></div>
      ) : (
        <button
          onClick={() => setAuthOpen(true)}
          className="mb-8 w-full rounded-2xl border border-dashed border-edge bg-surface/40 px-5 py-5 text-left font-body text-ash transition-colors hover:border-gold hover:text-gold"
          data-testid="discussion-signin"
        >
          {t("Sign in to join the discussion")} {"\u2192"}
        </button>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center font-body text-ash" data-testid="discussion-empty">
          {t("No comments yet. Be the first to start the conversation.")}
        </p>
      ) : (
        <div className="space-y-5">
          {rows.map(({ comment, depth }) => (
            <CommentRow key={comment.id} comment={comment} depth={depth}
              onReact={onReact} onReply={reply} onEdit={onEdit} onDelete={onDelete} onReport={onReport} busy={busy} />
          ))}
        </div>
      )}
    </section>
  );
};
